import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  ModeToggle,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  Copy,
  Download,
  GitBranch,
  Layers,
  Lock,
  LogOut,
  Megaphone,
  Pencil,
  Plus,
  Scale,
  Trash2,
  Trophy,
  Unlock,
  Users,
} from "lucide-react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { competitionJudgePinLoginPath } from "@/modules/auth/presentation/lib/pin-login-shared";
import { useCompetitionAuth } from "@/modules/auth/presentation/model/useCompetitionAuth";
import { AdminChangePinDialog } from "@/modules/auth/presentation/ui/AdminChangePinDialog";
import type { JudgeRoleId } from "@/modules/events/presentation/api/client";
import {
  useAdminEventDetailQuery,
  useAdminJudgeProfilesQuery,
  usePatchJudgeProfileMutation,
} from "@/modules/events/presentation/api/queries";
import { RankIcon } from "@/shared/ui/RankIcon";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import type { EventDivisionRow, Team } from "../api/client";
import {
  useCloseDivisionRound,
  useCreateDivision,
  useCreateDivisionCriterion,
  useCreateTeam,
  useDeleteDivision,
  useDeleteDivisionCriterion,
  useDeleteTeam,
  useDivisionsQuery,
  usePatchDivisionRoundConfig,
  usePublishResults,
  useReopenScoring,
  useResultsQuery,
  useSettingsQuery,
  useTeamsQuery,
  useToggleScoring,
  useUpdateTeam,
} from "../api/queries";
import { exportResultsToExcel } from "../lib/exportResults";
import {
  flattenAllRoundResults,
  flattenEventTeamResults,
} from "../lib/flattenEventResults";
import { ScoringReportTab } from "./ScoringReportTab";

type AdminWorkspaceValue = {
  eventId: string;
  eventSlug: string;
  eventName: string;
};

const AdminWorkspaceContext = createContext<AdminWorkspaceValue | null>(null);

function useAdminWorkspace(): AdminWorkspaceValue {
  const ctx = useContext(AdminWorkspaceContext);
  if (!ctx) {
    throw new Error("useAdminWorkspace used outside provider");
  }
  return ctx;
}

// ─── Header ───────────────────────────────────────────────────────────────────

function AdminWorkspaceHeader({
  eventName,
  onBack,
  onSignOut,
}: {
  eventName: string;
  onBack: () => void;
  onSignOut: () => void;
}) {
  return (
    <header className="kpop-bg flex flex-col gap-3 border-border border-b bg-card px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:px-6 sm:py-4">
      <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-center sm:gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="-ml-2 w-fit shrink-0 gap-1 sm:w-auto"
          onClick={onBack}
        >
          <ArrowLeft className="size-4 shrink-0" />
          ກັບໜ້າງານ
        </Button>
        <div className="min-w-0 sm:pb-px">
          <p className="font-bold text-accent text-xs uppercase tracking-widest">
            KiDS EXPO 2026
          </p>
          <h1 className="kpop-gradient-text wrap-break-word font-extrabold text-lg leading-tight sm:truncate sm:text-xl">
            {eventName}
          </h1>
        </div>
      </div>
      <div className="flex w-full shrink-0 flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
        <AdminChangePinDialog />
        <ModeToggle />
        <Button variant="ghost" size="sm" onClick={onSignOut}>
          <LogOut data-icon="inline-start" />
          ອອກຈາກລະບົບ
        </Button>
      </div>
    </header>
  );
}

// ─── Status Card ──────────────────────────────────────────────────────────────

function StatusCard({
  label,
  value,
  badge,
}: {
  label: string;
  value: string;
  badge?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1 rounded-xl bg-card p-4 ring-1 ring-border">
      <span className="text-muted-foreground text-xs">{label}</span>
      <div className="flex items-center gap-2">
        <span className="font-bold text-foreground text-lg">{value}</span>
        {badge}
      </div>
    </div>
  );
}

function DivisionRoundControls({
  eventId,
  division,
}: {
  eventId: string;
  division: EventDivisionRow;
}) {
  const patchRound = usePatchDivisionRoundConfig(eventId);
  const closeRound = useCloseDivisionRound(eventId);

  const [rc, setRc] = useState(() => Math.max(1, division.roundCount ?? 1));
  const [minScores, setMinScores] = useState<number[]>(() => {
    const need = Math.max(0, (division.roundCount ?? 1) - 1);
    const sorted = [...(division.qualifiers ?? [])].sort(
      (a, b) => a.fromRound - b.fromRound,
    );
    return Array.from({ length: need }, (_, i) => sorted[i]?.minScore ?? 75);
  });
  const [closeAfterRound, setCloseAfterRound] = useState(1);

  useEffect(() => {
    const need = Math.max(0, rc - 1);
    setMinScores((prev) => {
      const next = [...prev.slice(0, need)];
      while (next.length < need) next.push(75);
      return next;
    });
    setCloseAfterRound((r) => (rc > 1 ? Math.min(Math.max(1, r), rc - 1) : 1));
  }, [rc]);

  const maxCloseEligible = Math.max(0, (division.roundCount ?? 1) - 1);

  const closeRoundOptions = useMemo(
    () =>
      Array.from({ length: maxCloseEligible }, (_, i) => ({
        value: String(i + 1),
        label: `ຫຼັງຮອບ ${i + 1}`,
      })),
    [maxCloseEligible],
  );

  return (
    <div className="rounded-lg bg-gradient-to-br from-muted/50 via-muted/30 to-muted/15 p-4 ring-1 ring-border/60 dark:from-muted/25 dark:via-muted/15 dark:to-transparent">
      <div className="mb-3 flex items-center gap-2">
        <span className="flex size-7 items-center justify-center rounded-md bg-accent/15 ring-1 ring-accent/20">
          <GitBranch className="size-3.5 text-accent" aria-hidden />
        </span>
        <p className="font-semibold text-foreground text-xs tracking-wide">
          ກຳນົດຮອບ ແລະ ຄັດເຂົ້າຮອບຕໍ່
        </p>
      </div>
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        <div>
          <Label className="text-muted-foreground text-xs">ຈຳນວນຮອບ</Label>
          <Input
            type="number"
            min={1}
            max={20}
            className="mt-1 h-9 w-[5.5rem]"
            value={rc}
            onChange={(e) => {
              const n = Number.parseInt(e.target.value, 10);
              setRc(Number.isInteger(n) ? Math.min(20, Math.max(1, n)) : 1);
            }}
          />
        </div>
        {minScores.map((score, ix) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: stable index from round slot
          <div key={ix}>
            <Label className="text-muted-foreground text-xs">
              ຄະແນນເຂົ້າຮອບ {ix + 2}
            </Label>
            <Input
              type="number"
              min={0}
              max={999}
              className="mt-1 h-9 w-[5.5rem]"
              value={score}
              onChange={(e) => {
                const n = Number.parseInt(e.target.value, 10);
                const nv = Number.isInteger(n)
                  ? Math.min(999, Math.max(0, n))
                  : 0;
                setMinScores((prev) => {
                  const copy = [...prev];
                  copy[ix] = nv;
                  return copy;
                });
              }}
            />
          </div>
        ))}
        <Button
          type="button"
          size="sm"
          className="h-9 shrink-0"
          disabled={patchRound.isPending}
          onClick={() =>
            patchRound.mutate({
              divisionId: division.id,
              body: {
                roundCount: rc,
                qualifiers: minScores.map((minScore, i) => ({
                  fromRound: i + 1,
                  minScore,
                })),
              },
            })
          }
        >
          ບັນທຶກຮູບແບບຮອບ
        </Button>
      </div>
      {maxCloseEligible > 0 ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
          <div className="max-w-[220px]">
            <Label className="text-muted-foreground text-xs">
              ປິດແລ້ວຄັດ ຫຼັງເຫັນຄະແນນຮອບ
            </Label>
            <div className="mt-1">
              <SimpleSelect
                value={String(closeAfterRound)}
                onValueChange={(v) =>
                  setCloseAfterRound(Number.parseInt(v, 10) || 1)
                }
                options={closeRoundOptions}
              />
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={closeRound.isPending || maxCloseEligible < 1}
            className="h-9 shrink-0"
            onClick={() =>
              closeRound.mutate({
                divisionId: division.id,
                finishedRound: closeAfterRound,
              })
            }
          >
            ຄັດເຂົ້າຮອບຖັດໄປ
          </Button>
        </div>
      ) : null}
      <p className="mt-3 border-accent/25 border-border border-l-2 pl-3 text-[11px] text-muted-foreground italic leading-snug dark:border-accent/35">
        ບັນທຶກຈຳນວນຮອບ ແລະ ຄະແນນຂັ້ນຕ່ຳກ່ອນ ປົດປຸ່ມຄັດ.
      </p>
    </div>
  );
}

function DivisionCriteriaControls({
  eventId,
  division,
}: {
  eventId: string;
  division: EventDivisionRow;
}) {
  const createCriterion = useCreateDivisionCriterion(eventId);
  const deleteCriterion = useDeleteDivisionCriterion(eventId);
  const [judgeRole, setJudgeRole] = useState<"judge1" | "judge2" | "judge3">(
    "judge1",
  );
  const [name, setName] = useState("");
  const [maxScore, setMaxScore] = useState("10");

  const criteria = useMemo(
    () =>
      [...(division.extraCriteria ?? [])].sort((a, b) =>
        a.sortOrder === b.sortOrder
          ? a.createdAt.localeCompare(b.createdAt)
          : a.sortOrder - b.sortOrder,
      ),
    [division.extraCriteria],
  );

  const judgeOptions = [
    { value: "judge1", label: "Judge 1" },
    { value: "judge2", label: "Judge 2" },
    { value: "judge3", label: "Judge 3" },
  ];

  const addCriterion = () => {
    const trimmed = name.trim();
    const max = Number.parseInt(maxScore, 10);
    if (!trimmed || !Number.isInteger(max) || max < 1) {
      toast.error("ກະລຸນາໃສ່ criteria ແລະ ຄະແນນເຕັມ");
      return;
    }
    createCriterion.mutate(
      {
        divisionId: division.id,
        body: {
          judgeRole,
          name: trimmed,
          maxScore: max,
        },
      },
      {
        onSuccess: () => {
          setName("");
          setMaxScore("10");
        },
      },
    );
  };

  return (
    <div className="mt-3 rounded-lg bg-muted/25 p-4 ring-1 ring-border/60">
      <div className="mb-3 flex items-center justify-between gap-2">
        <p className="font-semibold text-foreground text-xs tracking-wide">
          Criteria ພິເສດ
        </p>
        <Badge variant="outline" className="tabular-nums">
          {criteria.length}
        </Badge>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="min-w-0 flex-1">
          <Label className="text-muted-foreground text-xs">ຊື່ criteria</Label>
          <Input
            className="mt-1 h-9"
            value={name}
            placeholder="ເຊັ່ນ Bonus Performance"
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="sm:w-32">
          <Label className="text-muted-foreground text-xs">ຄະແນນເຕັມ</Label>
          <Input
            className="mt-1 h-9"
            type="number"
            min={1}
            max={999}
            value={maxScore}
            onChange={(e) => setMaxScore(e.target.value)}
          />
        </div>
        <div className="sm:w-36">
          <Label className="text-muted-foreground text-xs">Judge</Label>
          <div className="mt-1">
            <SimpleSelect
              value={judgeRole}
              onValueChange={(v) =>
                setJudgeRole(v as "judge1" | "judge2" | "judge3")
              }
              options={judgeOptions}
            />
          </div>
        </div>
        <Button
          type="button"
          size="sm"
          className="h-9"
          disabled={createCriterion.isPending}
          onClick={addCriterion}
        >
          <Plus data-icon="inline-start" />
          ເພີ່ມ
        </Button>
      </div>
      {criteria.length > 0 ? (
        <ul className="mt-3 flex flex-col gap-2">
          {criteria.map((criterion) => (
            <li
              key={criterion.id}
              className="flex items-center justify-between gap-3 rounded-md bg-background px-3 py-2 ring-1 ring-border"
            >
              <div className="min-w-0">
                <p className="truncate font-medium text-foreground text-sm">
                  {criterion.name}
                </p>
                <p className="text-muted-foreground text-xs">
                  {criterion.judgeRole.replace("judge", "Judge ")} ·{" "}
                  {criterion.maxScore} ຄະແນນ
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8 shrink-0 text-destructive"
                disabled={deleteCriterion.isPending}
                onClick={() =>
                  deleteCriterion.mutate({
                    divisionId: division.id,
                    criterionId: criterion.id,
                  })
                }
              >
                <Trash2 className="size-4" />
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-muted-foreground text-xs">
          ຍັງບໍ່ມີ criteria ພິເສດສຳລັບຮາງນີ້
        </p>
      )}
    </div>
  );
}

// ─── Dashboard Tab ────────────────────────────────────────────────────────────

function DashboardTab() {
  const { eventId } = useAdminWorkspace();
  const { data: settings, isLoading: settingsLoading } =
    useSettingsQuery(eventId);
  const { data: results } = useResultsQuery(eventId);
  const { data: teams } = useTeamsQuery(eventId);
  const toggleMutation = useToggleScoring(eventId);
  const publishMutation = usePublishResults(eventId);
  const reopenMutation = useReopenScoring(eventId);

  const flatResults = useMemo(
    () => flattenEventTeamResults(results),
    [results],
  );
  const poolTotal = flatResults.length;

  const judge1Done = flatResults.filter((r) => r.judge1.submitted).length;
  const judge2Done = flatResults.filter((r) => r.judge2.submitted).length;
  const judge3Done = flatResults.filter((r) => r.judge3.submitted).length;
  const totalAllTeams = teams?.length ?? 0;

  if (settingsLoading) {
    return (
      <div className="flex flex-col gap-4 p-6">
        {Array.from({ length: 4 }).map((_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholders
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Status Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatusCard
          label="ສະຖານະ"
          value={settings?.isOpen ? "ເປີດຢູ່" : "ປິດຢູ່"}
          badge={
            <Badge variant={settings?.isOpen ? "default" : "secondary"}>
              {settings?.isOpen ? "ເປີດ" : "ປິດ"}
            </Badge>
          }
        />
        <StatusCard
          label="ຜົນ"
          value={settings?.resultsShown ? "ປະກາດແລ້ວ" : "ຍັງບໍ່ປະກາດ"}
          badge={
            <Badge variant={settings?.resultsShown ? "default" : "outline"}>
              {settings?.resultsShown ? "ສາທາລະນະ" : "ເຊື່ອງຢູ່"}
            </Badge>
          }
        />
        <StatusCard label="ທີມທັງໝົດ" value={String(totalAllTeams)} />
        <StatusCard
          label="ຄະແນນຄົບ"
          value={`${Math.min(judge1Done, judge2Done, judge3Done)} / ${poolTotal}`}
        />
      </div>

      {/* Judge Progress */}
      <div className="rounded-xl bg-card p-4 ring-1 ring-border">
        <p className="mb-3 font-semibold text-foreground text-sm">
          ຄວາມຄືບໜ້າການໃຫ້ຄະແນນ
        </p>
        <div className="flex flex-col gap-2">
          {[
            { label: "ກຳມະການ 1 — Dance Skill & Technique", done: judge1Done },
            {
              label: "ກຳມະການ 2 — Stage Presence & Performance",
              done: judge2Done,
            },
            { label: "ກຳມະການ 3 — Concept & Creativity", done: judge3Done },
          ].map(({ label, done }) => (
            <div
              key={label}
              className="flex items-center justify-between gap-4"
            >
              <span className="text-muted-foreground text-sm">{label}</span>
              <div className="flex items-center gap-2">
                <div className="h-2 w-24 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{
                      width:
                        poolTotal > 0 ? `${(done / poolTotal) * 100}%` : "0%",
                    }}
                  />
                </div>
                <span className="w-10 text-right font-medium text-foreground text-xs">
                  {done}/{poolTotal}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant={settings?.isOpen ? "destructive" : "default"}
              className="flex-1"
              disabled={toggleMutation.isPending || settings?.resultsShown}
            >
              {settings?.isOpen ? (
                <Lock data-icon="inline-start" />
              ) : (
                <Unlock data-icon="inline-start" />
              )}
              {settings?.isOpen ? "ປິດການໃຫ້ຄະແນນ" : "ເປີດການໃຫ້ຄະແນນ"}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {settings?.isOpen ? "ປິດການໃຫ້ຄະແນນ?" : "ເປີດການໃຫ້ຄະແນນ?"}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {settings?.isOpen
                  ? "ກຳມະການຈະບໍ່ສາມາດໃສ່ຫຼືແກ້ໄຂຄະແນນໄດ້ຫຼັງຈາກນີ້."
                  : "ກຳມະການຈະສາມາດໃສ່ຄະແນນໄດ້ທັນທີ."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>ຍົກເລີກ</AlertDialogCancel>
              <AlertDialogAction onClick={() => toggleMutation.mutate()}>
                ຢືນຢັນ
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="outline"
              className="flex-1"
              disabled={
                publishMutation.isPending ||
                settings?.isOpen ||
                settings?.resultsShown
              }
            >
              <Megaphone data-icon="inline-start" />
              ປະກາດຜົນ
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>ປະກາດຜົນການແຂ່ງຂັນ?</AlertDialogTitle>
              <AlertDialogDescription>
                ຜົນຈະເຫັນໄດ້ສາທາລະນະ. ການກະທຳນີ້ບໍ່ສາມາດຍ້ອນກັບໄດ້.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>ຍົກເລີກ</AlertDialogCancel>
              <AlertDialogAction onClick={() => publishMutation.mutate()}>
                ປະກາດ
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {settings?.resultsShown && (
        <div className="flex flex-col items-center gap-3 rounded-xl bg-muted/30 p-4 ring-1 ring-border">
          <p className="text-center text-muted-foreground text-xs">
            ✓ ຜົນໄດ້ຖືກປະກາດແລ້ວ — ຫາກຕ້ອງການແກ້ໄຂຄະແນນ ໃຫ້ເປີດໃຫ້ຄະແນນຄືນ
            (ຜົນຈະຖືກເຊື່ອງຈາກສາທາລະນະຊົ່ວຄາວ)
          </p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={reopenMutation.isPending}
              >
                <Unlock data-icon="inline-start" />
                ເປີດໃຫ້ຄະແນນຄືນ
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>ເປີດໃຫ້ຄະແນນຄືນ?</AlertDialogTitle>
                <AlertDialogDescription>
                  ຜົນທີ່ປະກາດແລ້ວຈະຖືກເຊື່ອງຈາກສາທາລະນະ ແລະ
                  ກຳມະການຈະສາມາດແກ້ໄຂ/ໃສ່ຄະແນນໄດ້ອີກຄັ້ງ. ທ່ານສາມາດປະກາດຜົນຄືນໄດ້ພາຍຫຼັງ.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>ຍົກເລີກ</AlertDialogCancel>
                <AlertDialogAction onClick={() => reopenMutation.mutate()}>
                  ຢືນຢັນ
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
    </div>
  );
}

// ─── Team Dialog ──────────────────────────────────────────────────────────────

function TeamDialog({
  open,
  onOpenChange,
  team,
  divisions,
  teams,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  team?: Team;
  divisions: EventDivisionRow[];
  teams: Team[];
}) {
  const { eventId } = useAdminWorkspace();
  const sortedDivisions = useMemo(
    () => [...divisions].sort((a, b) => a.sortOrder - b.sortOrder),
    [divisions],
  );

  const [divisionId, setDivisionId] = useState(
    team?.divisionId ?? sortedDivisions[0]?.id ?? "",
  );
  const [name, setName] = useState(team?.name ?? "");
  const [order, setOrder] = useState("1");

  const createMutation = useCreateTeam(eventId);
  const updateMutation = useUpdateTeam(eventId);

  const isEdit = Boolean(team);
  const isPending = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    const div = team?.divisionId ?? sortedDivisions[0]?.id ?? "";
    setDivisionId(div);
    setName(team?.name ?? "");
    const inSame = teams.filter(
      (t) => t.divisionId === div && t.id !== team?.id,
    );
    const mx =
      inSame.length > 0 ? Math.max(...inSame.map((t) => t.orderNumber)) : 0;
    const fallbackOrder = mx + 1;
    setOrder(String(team?.orderNumber ?? fallbackOrder));
  }, [
    team?.id,
    team?.divisionId,
    team?.name,
    team?.orderNumber,
    sortedDivisions,
    teams,
  ]);

  const divisionOptions = useMemo(
    () =>
      sortedDivisions.map((d) => ({
        value: d.id,
        label: `${d.name} (${d.sortOrder})`,
      })),
    [sortedDivisions],
  );

  const handleSave = async () => {
    const orderNum = Number.parseInt(order, 10);
    if (!name.trim() || Number.isNaN(orderNum) || orderNum < 1 || !divisionId) {
      toast.error("ກະລຸນາໃສ່ຂໍ້ມູນໃຫ້ຄົບ");
      return;
    }
    if (isEdit && team) {
      await updateMutation.mutateAsync({
        id: team.id,
        divisionId: divisionId !== team.divisionId ? divisionId : undefined,
        name: name.trim(),
        orderNumber: orderNum,
      });
    } else {
      await createMutation.mutateAsync({
        divisionId,
        name: name.trim(),
        orderNumber: orderNum,
      });
    }
    onOpenChange(false);
    setName("");
  };

  if (sortedDivisions.length === 0) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? "ແກ້ໄຂທີມ" : "ເພີ່ມທີມໃໝ່"}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="team-division">ຮາງການແຂ່ງຂັນ</Label>
            <SimpleSelect
              value={divisionId}
              onValueChange={setDivisionId}
              options={divisionOptions}
              placeholder="ເລືອກຮາງ..."
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="team-order">ລຳດັບໃນຮາງ (ປ້ອງກັນຊໍ້າໃນຮາງດຽວກັນ)</Label>
            <Input
              id="team-order"
              type="number"
              min={1}
              value={order}
              onChange={(e) => setOrder(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="team-name">ຊື່ທີມ</Label>
            <Input
              id="team-name"
              placeholder="ຊື່ທີມ..."
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSave()}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            ຍົກເລີກ
          </Button>
          <Button onClick={handleSave} disabled={isPending}>
            {isPending ? "ກຳລັງບັນທຶກ..." : isEdit ? "ບັນທຶກ" : "ເພີ່ມ"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Teams Tab ────────────────────────────────────────────────────────────────

function TeamsTab() {
  const { eventId } = useAdminWorkspace();
  const { data: divisions, isLoading: divLoading } = useDivisionsQuery(eventId);
  const { data: teams, isLoading: teamsLoading } = useTeamsQuery(eventId);
  const { data: results } = useResultsQuery(eventId);
  const deleteMutation = useDeleteTeam(eventId);
  const createDivisionMut = useCreateDivision(eventId);
  const deleteDivisionMut = useDeleteDivision(eventId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTeam, setEditTeam] = useState<Team | undefined>(undefined);
  const [newDivisionName, setNewDivisionName] = useState("");

  const sortedDivisions = useMemo(
    () => [...(divisions ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [divisions],
  );

  const scoreMap = useMemo(() => {
    const flat = flattenEventTeamResults(results);
    return new Map(flat.map((r) => [r.id, r]));
  }, [results]);

  const openCreate = () => {
    setEditTeam(undefined);
    setDialogOpen(true);
  };

  const openEdit = (team: Team) => {
    setEditTeam(team);
    setDialogOpen(true);
  };

  const teamsByDivisionId = useMemo(() => {
    const m = new Map<string, Team[]>();
    for (const d of sortedDivisions) m.set(d.id, []);
    for (const team of teams ?? []) {
      const arr = m.get(team.divisionId);
      if (arr) arr.push(team);
      else m.set(team.divisionId, [team]);
    }
    for (const arr of m.values())
      arr.sort((a, b) => a.orderNumber - b.orderNumber);
    return m;
  }, [teams, sortedDivisions]);

  const isLoading = divLoading || teamsLoading;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 p-6">
        {Array.from({ length: 5 }).map((_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: skeleton
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="overflow-hidden rounded-2xl border border-border/70 bg-gradient-to-b from-card via-card to-muted/25 shadow-sm ring-1 ring-border/35 dark:to-muted/20">
        <div className="kpop-bg flex flex-col gap-4 border-border border-b px-4 py-4 sm:flex-row sm:items-start sm:gap-5 sm:px-5">
          <div className="flex min-w-0 flex-1 items-start gap-3 sm:gap-4">
            <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-accent/15 shadow-inner ring-1 ring-accent/30">
              <Layers
                className="size-[1.15rem] text-accent"
                strokeWidth={2}
                aria-hidden
              />
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 gap-y-1">
                <h2 className="font-semibold text-base text-foreground leading-tight tracking-tight">
                  ຮາງການແຂ່ງຂັນ
                </h2>
                <Badge variant="secondary" className="tabular-nums">
                  {sortedDivisions.length} ຮາງ
                </Badge>
              </div>
              <p className="mt-1.5 max-w-2xl text-muted-foreground text-xs leading-relaxed">
                ແບ່ງກຸ່ມທີມ (Junior / Open ແລະອື່ນໆ); ອັນດັບແຍກຕາມຮາງເທົ່ານັ້ນ.
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
            <Input
              className="h-11 min-h-11 border-border/80 bg-background/80 sm:max-w-md"
              placeholder="ຊື່ຮາງໃໝ່ ເຊັ່ນ Junior"
              value={newDivisionName}
              onChange={(e) => setNewDivisionName(e.target.value)}
            />
            <Button
              type="button"
              size="sm"
              className="h-11 shrink-0 px-4 font-semibold sm:self-start"
              disabled={createDivisionMut.isPending || !newDivisionName.trim()}
              onClick={() => {
                const nameTrim = newDivisionName.trim();
                if (!nameTrim) return;
                createDivisionMut.mutate(
                  { name: nameTrim },
                  { onSuccess: () => setNewDivisionName("") },
                );
              }}
            >
              <Plus data-icon="inline-start" className="size-4" />
              ເພີ່ມຮາງ
            </Button>
          </div>

          <ul className="mt-5 flex flex-col gap-3">
            {sortedDivisions.map((d, divisionIndex) => {
              const count = teamsByDivisionId.get(d.id)?.length ?? 0;
              const canRemove = count === 0;
              const rounds = Math.max(1, d.roundCount ?? 1);
              return (
                <li key={d.id}>
                  <div className="overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm transition-[box-shadow,border-color] duration-300 hover:border-accent/35 hover:shadow-md dark:bg-card dark:hover:border-accent/25">
                    <div className="flex flex-col gap-3 border-border border-b border-dashed px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:py-4 sm:pr-4">
                      <div className="flex min-w-0 flex-1 items-center gap-3">
                        <span
                          className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-accent/15 font-bold text-accent text-sm tabular-nums shadow-inner ring-1 ring-accent/25"
                          title={`ຈັດອັນດັບ ${divisionIndex + 1}`}
                        >
                          {divisionIndex + 1}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate font-semibold text-[0.9375rem] text-foreground leading-snug">
                            {d.name}
                          </p>
                          <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
                            <span className="tabular-nums">{count} ທີມ</span>
                            <span
                              aria-hidden
                              className="text-[10px] text-border"
                            >
                              ●
                            </span>
                            <span className="tabular-nums">{rounds} ຮອບ</span>
                          </p>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2 self-end sm:self-auto">
                        <Badge
                          variant={count === 0 ? "outline" : "default"}
                          className="hidden tabular-nums sm:inline-flex"
                        >
                          {count} ທີມ
                        </Badge>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={
                                deleteDivisionMut.isPending || !canRemove
                              }
                              className={cn(
                                "h-9 gap-1.5 px-3",
                                !canRemove &&
                                  "border-transparent text-muted-foreground opacity-70",
                              )}
                            >
                              <Trash2 className="size-3.5 shrink-0" />
                              ລຶບ
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>ລຶບຮາງນີ້?</AlertDialogTitle>
                              <AlertDialogDescription>
                                ອະນຸຍາດສະເພາະເມື່ອຍັງບໍ່ມີທີມໃນຮາງດັ່ງກ່າວ
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>ຍົກເລີກ</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                onClick={() => deleteDivisionMut.mutate(d.id)}
                              >
                                ລຶບ
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                    <div className="px-3 pt-1 pb-3 sm:px-4">
                      <DivisionRoundControls
                        key={`${d.id}:${d.updatedAt}`}
                        eventId={eventId}
                        division={d}
                      />
                      <DivisionCriteriaControls
                        key={`${d.id}:criteria:${d.extraCriteria.length}`}
                        eventId={eventId}
                        division={d}
                      />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          {sortedDivisions.length === 0 ? (
            <div className="mt-5 rounded-xl bg-muted/30 px-4 py-8 text-center ring-1 ring-border">
              <Layers className="mx-auto mb-2 size-8 text-muted-foreground" />
              <p className="font-medium text-foreground text-sm">
                ຍັງບໍ່ມີຮາງການແຂ່ງຂັນ
              </p>
              <p className="mt-1 text-muted-foreground text-xs">
                ເພີ່ມຮາງການແຂ່ງຂັນກ່ອນ ແລ້ວຈຶ່ງເພີ່ມທີມໄດ້.
              </p>
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <p className="font-semibold text-foreground">
          ທີມທັງໝົດ ({teams?.length ?? 0})
        </p>
        <Button
          size="sm"
          onClick={openCreate}
          disabled={sortedDivisions.length === 0}
        >
          <Plus data-icon="inline-start" />
          ເພີ່ມທີມ
        </Button>
      </div>

      {sortedDivisions.length === 0 ? (
        <div className="rounded-xl bg-card py-12 text-center ring-1 ring-border">
          <Users className="mx-auto mb-2 size-8 text-muted-foreground" />
          <p className="text-muted-foreground text-sm">
            ຕ້ອງເພີ່ມຮາງການແຂ່ງຂັນກ່ອນ ຈຶ່ງຈະເພີ່ມທີມໄດ້
          </p>
        </div>
      ) : teams?.length === 0 ? (
        <div className="rounded-xl bg-card py-12 text-center ring-1 ring-border">
          <Users className="mx-auto mb-2 size-8 text-muted-foreground" />
          <p className="text-muted-foreground text-sm">ຍັງບໍ່ມີທີມ</p>
          <Button
            size="sm"
            variant="ghost"
            className="mt-3"
            onClick={openCreate}
          >
            ເພີ່ມທີມທຳອິດ
          </Button>
        </div>
      ) : (
        sortedDivisions.map((div) => {
          const slice = teamsByDivisionId.get(div.id) ?? [];
          return (
            <div key={div.id} className="flex flex-col gap-2">
              <h3 className="font-semibold text-foreground text-sm">
                {div.name}{" "}
                <span className="font-normal text-muted-foreground tabular-nums">
                  ({slice.length})
                </span>
              </h3>
              <div className="overflow-hidden rounded-xl ring-1 ring-border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-border border-b bg-muted/40 text-left text-muted-foreground">
                      <th className="px-4 py-3 font-medium">#</th>
                      <th className="px-4 py-3 font-medium">ຊື່ທີມ</th>
                      <th className="px-4 py-3 text-center font-medium">J1</th>
                      <th className="px-4 py-3 text-center font-medium">J2</th>
                      <th className="px-4 py-3 text-center font-medium">J3</th>
                      <th className="px-4 py-3 text-center font-medium">ລວມ</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {slice.map((team, idx) => {
                      const score = scoreMap.get(team.id);
                      return (
                        <tr
                          key={team.id}
                          className={cn(
                            "border-border border-b last:border-0",
                            idx % 2 === 0 ? "bg-card" : "bg-muted/20",
                          )}
                        >
                          <td className="px-4 py-3 text-muted-foreground tabular-nums">
                            {team.orderNumber}
                          </td>
                          <td className="px-4 py-3 font-medium text-foreground">
                            {team.name}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <ScoreCell
                              value={score?.judge1.total}
                              submitted={score?.judge1.submitted}
                            />
                          </td>
                          <td className="px-4 py-3 text-center">
                            <ScoreCell
                              value={score?.judge2.total}
                              submitted={score?.judge2.submitted}
                            />
                          </td>
                          <td className="px-4 py-3 text-center">
                            <ScoreCell
                              value={score?.judge3.total}
                              submitted={score?.judge3.submitted}
                            />
                          </td>
                          <td className="px-4 py-3 text-center font-bold text-foreground">
                            {score ? score.grandTotal : "—"}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-1">
                              <Button
                                size="icon"
                                variant="ghost"
                                className="size-8"
                                onClick={() => openEdit(team)}
                              >
                                <Pencil className="size-3.5" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="size-8 text-destructive hover:text-destructive"
                                  >
                                    <Trash2 className="size-3.5" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>ລຶບທີມ?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      ທີມ <strong>{team.name}</strong>{" "}
                                      ແລະຄະແນນທັງໝົດຈະຖືກລຶບຖາວອນ.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>ຍົກເລີກ</AlertDialogCancel>
                                    <AlertDialogAction
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                      onClick={() =>
                                        deleteMutation.mutate(team.id)
                                      }
                                    >
                                      ລຶບ
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })
      )}

      <TeamDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        team={editTeam}
        divisions={sortedDivisions}
        teams={teams ?? []}
      />
    </div>
  );
}

const JUDGE_SLOT_META: {
  role: JudgeRoleId;
  judgeNum: 1 | 2 | 3;
  title: string;
  subject: string;
}[] = [
  {
    role: "judge1",
    judgeNum: 1,
    title: "Judge 1",
    subject: "Dance Skill & Technique",
  },
  {
    role: "judge2",
    judgeNum: 2,
    title: "Judge 2",
    subject: "Stage Presence & Performance",
  },
  {
    role: "judge3",
    judgeNum: 3,
    title: "Judge 3",
    subject: "Concept & Creativity",
  },
];

// ─── Judges Tab (display names & admin notes) ────────────────────────────────

function JudgesTab() {
  const { eventId, eventName } = useAdminWorkspace();
  const { data: profiles, isLoading } = useAdminJudgeProfilesQuery(eventId);
  const patchMutation = usePatchJudgeProfileMutation(eventId);
  const [draftByRole, setDraftByRole] = useState<
    Record<string, { displayName: string; notes: string }>
  >({});

  useEffect(() => {
    if (!profiles) return;
    const next: Record<string, { displayName: string; notes: string }> = {};
    for (const p of profiles) {
      next[p.role] = {
        displayName: p.displayName ?? "",
        notes: p.notes ?? "",
      };
    }
    setDraftByRole(next);
  }, [profiles]);

  const setDraft = (
    role: JudgeRoleId,
    field: "displayName" | "notes",
    value: string,
  ) => {
    setDraftByRole((prev) => ({
      ...prev,
      [role]: {
        displayName: prev[role]?.displayName ?? "",
        notes: prev[role]?.notes ?? "",
        [field]: value,
      },
    }));
  };

  const saveRole = async (role: JudgeRoleId) => {
    const d = draftByRole[role];
    if (d === undefined) return;
    await patchMutation.mutateAsync({
      role,
      body: {
        displayName: d.displayName.trim() === "" ? null : d.displayName.trim(),
        notes: d.notes.trim() === "" ? null : d.notes.trim(),
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 p-6">
        {Array.from({ length: 3 }).map((_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: skeleton
          <Skeleton key={i} className="h-40 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      <p className="text-muted-foreground text-sm leading-relaxed">
        ແຕ່ລະກຳມະມີ
        <strong className="text-foreground">ລິ້ງເຂົ້າລະບົບສະເພາະຕົວເອງ</strong>{" "}
        (ສົ່ງໃຫ້ກຳມະການແຕ່ລະຄົນ). ຜູ້ຄຸ້ມຄອງໃຊ້ໜ້າ{" "}
        <span className="font-mono text-foreground text-xs">
          /competition/login
        </span>
        .
      </p>
      <p className="text-muted-foreground text-sm">
        ຊື່ສະແດງຈະປະກົດໃຫ້ກຳມະການເຫັນຕອນເຂົ້າລະບົບ (ບໍ່ສົ່ງຫມາຍເຫດ admin ໃຫ້ສາທາລະນະ)
      </p>
      {JUDGE_SLOT_META.map((slot) => {
        const d = draftByRole[slot.role] ?? { displayName: "", notes: "" };
        const judgeHref = competitionJudgePinLoginPath(eventId, slot.judgeNum);
        const judgeAbs =
          typeof window !== "undefined"
            ? `${window.location.origin}${judgeHref}`
            : judgeHref;
        const copyJudgeUrl = async () => {
          try {
            await navigator.clipboard.writeText(judgeAbs);
            toast.success("ຄັດລອກລິ້ງແລ້ວ");
          } catch {
            toast.error("ບໍ່ສາມາດຄັດລອກໄດ້");
          }
        };
        return (
          <div
            key={slot.role}
            className="flex flex-col gap-3 rounded-xl bg-card p-4 ring-1 ring-border"
          >
            <div className="flex items-start gap-2">
              <Scale className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-foreground">{slot.title}</p>
                <p className="text-muted-foreground text-xs">{slot.subject}</p>
              </div>
            </div>
            <div className="flex flex-col gap-2 rounded-lg bg-muted/30 p-3 ring-1 ring-border">
              <Label className="text-muted-foreground text-xs">
                ລິ້ງເຂົ້າລະບົບ {slot.title} — {eventName}
              </Label>
              <code className="max-h-24 overflow-auto break-all rounded-md bg-background px-2 py-1.5 font-mono text-[0.65rem] text-foreground leading-snug sm:text-xs">
                {judgeAbs}
              </code>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="w-fit gap-1.5"
                onClick={() => void copyJudgeUrl()}
              >
                <Copy className="size-3.5" />
                ຄັດລອກລິ້ງ
              </Button>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor={`jn-${slot.role}-name`}>ຊື່ສະແດງ (public)</Label>
              <Input
                id={`jn-${slot.role}-name`}
                value={d.displayName}
                onChange={(e) =>
                  setDraft(slot.role, "displayName", e.target.value)
                }
                placeholder="ເຊັ່ນ: ປ້າຍຊື່ກຳມະການ"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor={`jn-${slot.role}-notes`}>
                ຫມາຍເຫດ (ເບິ່ງແຕ່ admin)
              </Label>
              <textarea
                id={`jn-${slot.role}-notes`}
                value={d.notes}
                onChange={(e) => setDraft(slot.role, "notes", e.target.value)}
                rows={2}
                className={cn(
                  "w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-foreground text-sm",
                  "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                )}
              />
            </div>
            <div>
              <Button
                type="button"
                size="sm"
                onClick={() => saveRole(slot.role)}
                disabled={patchMutation.isPending}
              >
                {patchMutation.isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ScoreCell({
  value,
  submitted,
}: {
  value?: number;
  submitted?: boolean;
}) {
  if (!submitted) return <span className="text-muted-foreground">—</span>;
  return (
    <Badge variant="secondary" className="tabular-nums">
      {value}
    </Badge>
  );
}

// ─── Results Tab ──────────────────────────────────────────────────────────────

function ResultsTab() {
  const { eventId, eventSlug, eventName } = useAdminWorkspace();
  const { data: resultsPayload, isLoading } = useResultsQuery(eventId);
  const flatCount = flattenAllRoundResults(resultsPayload).length;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 p-6">
        {Array.from({ length: 5 }).map((_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: skeleton
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (
    !resultsPayload?.divisions.some((s) =>
      s.rounds.some((r) => r.teams.length > 0),
    )
  ) {
    return (
      <div className="py-16 text-center">
        <Trophy className="mx-auto mb-3 size-10 text-muted-foreground" />
        <p className="text-muted-foreground text-sm">ຍັງບໍ່ມີຜົນ</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex items-center justify-between">
        <p className="font-semibold text-foreground">ຜົນທັງໝົດ ({flatCount})</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            exportResultsToExcel(resultsPayload, {
              eventName,
              eventSlug,
            })
          }
        >
          <Download data-icon="inline-start" />
          Export Excel
        </Button>
      </div>
      {resultsPayload.divisions.map((section) => {
        const sectionHasTeams = section.rounds.some((r) => r.teams.length > 0);
        if (!sectionHasTeams) return null;
        return (
          <div key={section.divisionId} className="flex flex-col gap-3">
            <h3 className="font-semibold text-foreground text-sm">
              {section.divisionName}
            </h3>
            {section.rounds.map((rnd) =>
              rnd.teams.length === 0 ? null : (
                <div key={`${section.divisionId}-r${rnd.roundIndex}`}>
                  {section.roundCount > 1 ? (
                    <h4 className="mb-2 font-medium text-muted-foreground text-xs">
                      ຮອບ {rnd.roundIndex}{" "}
                      <span className="tabular-nums">
                        ({rnd.teams.length} ທີມ)
                      </span>
                    </h4>
                  ) : null}
                  <div className="overflow-hidden rounded-xl ring-1 ring-border">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-border border-b bg-muted/40 text-left text-muted-foreground">
                          <th className="px-4 py-3 font-medium">ອັນດັບຮາງ</th>
                          <th className="px-4 py-3 font-medium">ທີມ</th>
                          <th className="px-4 py-3 text-center font-medium">
                            J1 (30)
                          </th>
                          <th className="px-4 py-3 text-center font-medium">
                            J2 (30)
                          </th>
                          <th className="px-4 py-3 text-center font-medium">
                            J3 (40)
                          </th>
                          <th className="px-4 py-3 text-center font-medium">
                            ລວມ
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {rnd.teams.map((r, idx) => (
                          <tr
                            key={`${section.divisionId}-${rnd.roundIndex}-${r.id}`}
                            className={cn(
                              "border-border border-b last:border-0",
                              r.rank === 1 && "bg-chart-5/10",
                              r.rank === 2 && "bg-muted/30",
                              r.rank === 3 && "bg-chart-3/10",
                              r.rank > 3 &&
                                (idx % 2 === 0 ? "bg-card" : "bg-muted/20"),
                            )}
                          >
                            <td className="px-4 py-3 text-foreground">
                              <RankIcon rank={r.rank} />
                            </td>
                            <td className="px-4 py-3 font-medium text-foreground">
                              <span className="mr-2 text-muted-foreground text-xs tabular-nums">
                                #{r.orderNumber}
                              </span>
                              {r.name}
                            </td>
                            <td className="px-4 py-3 text-center text-foreground">
                              {r.judge1.total}
                            </td>
                            <td className="px-4 py-3 text-center text-foreground">
                              {r.judge2.total}
                            </td>
                            <td className="px-4 py-3 text-center text-foreground">
                              {r.judge3.total}
                            </td>
                            <td className="px-4 py-3 text-center font-bold text-foreground">
                              {r.grandTotal}/{r.maxTotal}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ),
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Event workspace ------------------------------------------------------------

export function EventAdminWorkspacePage() {
  const params = useParams({ strict: false }) as { eventId?: string };
  const eventId = params.eventId;
  const { signOut, isCompetitionAdmin } = useCompetitionAuth();
  const navigate = useNavigate();
  const {
    data: event,
    isPending: eventPending,
    isError,
  } = useAdminEventDetailQuery(eventId);

  const handleSignOut = async () => {
    await signOut();
    navigate({ to: "/competition/login" });
  };

  const handleBack = () => {
    navigate({ to: "/competition/admin" });
  };

  useEffect(() => {
    if (!eventId || eventPending) return;
    if (isError || !event) {
      navigate({ to: "/competition/admin" });
    }
  }, [eventId, eventPending, isError, event, navigate]);

  if (!isCompetitionAdmin || !eventId) {
    return null;
  }

  if (eventPending) {
    return (
      <div className="flex min-h-svh flex-col bg-background">
        <AdminWorkspaceHeader
          eventName="…"
          onBack={handleBack}
          onSignOut={handleSignOut}
        />
        <div className="flex flex-1 flex-col gap-3 p-6">
          {Array.from({ length: 6 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: skeleton
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (!event) {
    return null;
  }

  const workspaceValue: AdminWorkspaceValue = {
    eventId: event.id,
    eventSlug: event.slug,
    eventName: event.name,
  };

  return (
    <AdminWorkspaceContext.Provider value={workspaceValue}>
      <div className="flex min-h-svh flex-col bg-background">
        <AdminWorkspaceHeader
          eventName={event.name}
          onBack={handleBack}
          onSignOut={handleSignOut}
        />

        <main className="flex-1">
          <Tabs defaultValue="dashboard" className="flex flex-col">
            <div className="border-border border-b bg-card px-6">
              <TabsList className="h-auto rounded-none border-0 bg-transparent p-0">
                <TabsTrigger
                  value="dashboard"
                  className="rounded-none border-transparent border-b-2 py-3 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                >
                  ໜ້າຫຼັກ
                </TabsTrigger>
                <TabsTrigger
                  value="teams"
                  className="rounded-none border-transparent border-b-2 py-3 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                >
                  ທີມ
                </TabsTrigger>
                <TabsTrigger
                  value="judges"
                  className="rounded-none border-transparent border-b-2 py-3 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                >
                  ກຳມະການ
                </TabsTrigger>
                <TabsTrigger
                  value="results"
                  className="rounded-none border-transparent border-b-2 py-3 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                >
                  ຜົນ
                </TabsTrigger>
                <TabsTrigger
                  value="report"
                  className="rounded-none border-transparent border-b-2 py-3 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                >
                  ລາຍງານ
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="dashboard" className="m-0">
              <DashboardTab />
            </TabsContent>
            <TabsContent value="teams" className="m-0">
              <TeamsTab />
            </TabsContent>
            <TabsContent value="judges" className="m-0">
              <JudgesTab />
            </TabsContent>
            <TabsContent value="results" className="m-0">
              <ResultsTab />
            </TabsContent>
            <TabsContent value="report" className="m-0">
              <ScoringReportTab
                eventId={event.id}
                eventName={event.name}
                eventSlug={event.slug}
              />
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </AdminWorkspaceContext.Provider>
  );
}
